import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { FormControl } from '@angular/forms';
import { deofuscate } from '@/utils/obfuscation';
import { BoxColors } from '@/shared/person-card/person-card.component';
import { Person } from '@/interfaces/person';
import { PersonService } from '@/services/person/person.service';
import { AuthService } from '@/auth/service/auth.service';
import { Store } from '@ngrx/store';
import { StoreState } from '@/store';
import { getCurrentUserData } from '@/store/login/selectors';
import { combineLatest } from 'rxjs';
import { LoginResponse } from '@/auth/interface/auth.interface';

type View = 'requests' | 'licenses' | 'infractions';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit {
  currentViewControl: FormControl = new FormControl('');
  availableViews: View[] = [];
  currentView?: View;

  routeControl: FormControl = new FormControl();
  personBoxColor: BoxColors = BoxColors.BROWN_GREY;
  selectedPersonData!: Person;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private store: Store<StoreState>,
    private personService: PersonService,
  ) {

  }

  ngOnInit(): void {
    this.route.params.pipe().subscribe(({ ofuscatedData }) => {
      const params = deofuscate(ofuscatedData);
      this.personService.getPersonDataById(params.personId).subscribe({
        next: (personData) => {
          this.selectedPersonData = personData;
        },
      });

      this.currentView = this.router.url.split('/').reverse()[0] as View;
      this.drawComponents();

      this.setCurrentView(this.currentView);

      const userData$ = getCurrentUserData(this.store);
      const routeChangeAndUserData$ = combineLatest(this.router.events, userData$);

      userData$.subscribe({
        next: () => {
          this.currentView = this.router.url.split('/').reverse()[0] as View;
          this.setCurrentView(this.currentView);
          this.drawComponents();
        },
      });

      routeChangeAndUserData$.subscribe({
        next: ([val, userData]: [any, LoginResponse]) => {
          const routeEventChange = <NavigationEnd>val;
          if (routeEventChange.url) {
            if (routeEventChange.url.includes('/search/driver/result/')) {
              this.setCurrentView(routeEventChange.url);
              this.drawComponents();
            }
          }
        },
      });

      this.currentViewControl.valueChanges.subscribe((newView) => {
        if (newView !== this.currentView) {
          const [currentView, ...otherPartOfTheRoute] = this.router.url.split('/').reverse();
          const newRoute = [newView, ...otherPartOfTheRoute].reverse().join('/');
          this.currentView = newView;
          this.router.navigateByUrl(newRoute);
        }
      });
    });
  }

  setCurrentView(newUrl?: string) {
    const views: View[] = ['infractions', 'licenses', 'requests'];
    if (newUrl) {
      const previousPath = this.router.url.split('/').reverse()[1];
      if (previousPath !== 'driver' && previousPath !== 'infraction') {
        const currentView = newUrl.split('/').reverse()[0];
        if (previousPath === 'result') {
          if (!views.includes(currentView as View) && currentView !== 'driver' && currentView !== 'infraction') {
            if (this.availableViews.length === 0) {
              this.router.navigateByUrl('/');
            }
            const newRoute = [...this.router.url.split('/'), this.availableViews[0]].join('/');
            this.router.navigateByUrl(newRoute);
          }
        }
        else {
          this.currentViewControl.patchValue(currentView);
        }
      }
    }
  }

  drawComponents() {
    const availableViews: View[] = [];
    if (this.auth.userHavePrivilege('SEARCH DRIVER') || this.auth.userHavePrivilege('APPROVAL SEARCH')) {
      availableViews.push('licenses');
    }
    if (this.auth.userHavePrivilege('SEARCH INFRINGEMENT') || this.auth.userHavePrivilege('INFRINGEMENT SEARCH')) {
      availableViews.push('infractions');
    }
    if (
      this.auth.userHavePrivilege('SEARCH DRIVER')
      || this.auth.userHavePrivilege('APPROVAL SEARCH')
      || this.auth.userHavePrivilege('SEARCH INFRINGEMENT')
      || this.auth.userHavePrivilege('INFRINGEMENT SEARCH')
    ) {
      availableViews.push('requests');
    }
    this.availableViews = availableViews;
  }

  searchAnotherPerson() {
    const [website, ...url] = window.location.href.split('//')[1].split('/');
    if (url.join('/').includes('search/infraction')) {
      this.router.navigateByUrl('/search/infraction');
    }
    else {
      this.router.navigateByUrl('/search/driver');
    }
  }

}
